import {
  fetchWithErrorHandling,
  parseJSONWithBetterErrors,
  throwErrorsIfBadResponse,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import {useMutation} from '@github-ui/react-query'
import {useDiffSummariesActions, useMarkersActions, useGetThread, usePendingReviewActions} from '../stores/selectors'
import type {FilesRoutePayload} from '../page-data/payloads/files'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'

export function useDeleteReviewCommentMutation({onSuccess}: {onSuccess?: () => void} = {}) {
  const {
    pullRequest: {pathName: basePath},
  } = useRoutePayload<FilesRoutePayload>()
  const {deleteThreadComment} = useMarkersActions()
  const {onCommentThreadDeleted} = useDiffSummariesActions()
  const getThread = useGetThread()
  const {removePendingComment} = usePendingReviewActions()

  return useMutation({
    mutationFn: async ({commentId, threadId}: {commentId: string; threadId: string; filePath: string}) => {
      const comment = getThread(Number(threadId))?.commentsData?.comments?.find(c => c.id === commentId)

      if (!comment) return

      const apiUrl = `${basePath}/page_data/review_comments/${comment.databaseId}`
      const response = await fetchWithErrorHandling(`${apiUrl}`, {
        method: 'DELETE',
      })

      if (response.status === 204 || response.status === 200) {
        return
      }

      // If we don't return a 204 (no content), we need to parse the response
      // and render the errors
      const json = await parseJSONWithBetterErrors(response)
      throwErrorsIfBadResponse(response, json)
    },

    onSuccess: (_, {commentId, threadId, filePath}) => {
      const result = deleteThreadComment(Number(threadId), commentId)

      removePendingComment(Number(threadId))

      if (result !== 'thread_deleted') {
        return
      }

      if (result === 'thread_deleted') {
        onCommentThreadDeleted({
          path: filePath,
          threadID: threadId,
        })
      }

      onSuccess?.()
    },
  })
}
